import type { IncomingHttpHeaders } from 'http';
import type { AxiosResponse } from 'axios';
import getConfig from 'next/config';

import { donedealServicesGateway } from '../api/api-utils/commonApiUtils';
import type {
  IGetAdsRequest,
  IGetAdsResponseV2,
  IGetFiltersResponse,
  GetFiltersWithCountRequest,
  GetModelsWithCountsRequest,
  GetTrimsWithCountsRequest,
} from '../api/types/searchPageApiTypes';
import { IGetAdsResponse, IGetSection } from '../api/types/searchPageApiTypes';
import { IGetSearchFilterMakeModel } from '../types';

const {
  publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
} = getConfig();

async function getAds(
  payload: IGetAdsRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: string,
): Promise<AxiosResponse<IGetAdsResponse>> {
  const response: AxiosResponse<IGetAdsResponse> =
    await donedealServicesGateway.post(`/ddapi/v1/search`, payload, {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
    });

  return response;
}

async function getAdsV2(
  payload: IGetAdsRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: string,
): Promise<AxiosResponse<IGetAdsResponseV2>> {
  const response: AxiosResponse<IGetAdsResponseV2> =
    await donedealServicesGateway.post(`/ddapi/v2/search`, payload, {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
    });

  return response;
}

async function getFiltersWithCount({
  section,
  headers = { accept: 'application/json' },
  payload,
  timeout = parseInt(API_LISTINGS_TIMEOUT),
}: GetFiltersWithCountRequest): Promise<AxiosResponse<IGetFiltersResponse>> {
  return await donedealServicesGateway.post(
    `/ddapi/v4/search/filters/${section}`,
    {
      ...payload,
      sections: [section],
    },
    {
      headers: {
        ...headers,
      },
      timeout,
    },
  );
}

async function getFiltersDealerShowroom(
  section: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  dealerId: string | null = null,
  timeout?: string,
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const response: AxiosResponse<IGetFiltersResponse> =
    await donedealServicesGateway.get(
      `/ddapi/v1/search/showroom/filters/${section}?dealerId=${dealerId}`,
      {
        headers: {
          ...headers,
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );

  return response;
}

async function getCarsFiltersWithCount(
  timeout: number = parseInt(API_LISTINGS_TIMEOUT),
  body: {
    ranges?: {
      from: string | undefined;
      to: string | undefined;
      name: string;
    }[];
  } = {},
): Promise<AxiosResponse<IGetFiltersResponse>> {
  return await donedealServicesGateway.post(
    `/ddapi/v4/search/filters/cars`,
    {
      sections: ['cars'],
      ...body,
    },
    {
      headers: { accept: 'application/json' },
      timeout,
    },
  );
}

async function getFilters(
  section: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout: number = parseInt(API_LISTINGS_TIMEOUT),
): Promise<AxiosResponse<IGetFiltersResponse>> {
  return await donedealServicesGateway.get(
    `/ddapi/v4/search/filters/${section}`,
    {
      headers: {
        ...headers,
      },
      timeout,
    },
  );
}

async function getFiltersC2B(
  headers: IncomingHttpHeaders = { accept: 'application/json' },
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const response: AxiosResponse<IGetFiltersResponse> =
    await donedealServicesGateway.get('ddapi/v1/search/filters/cars-c2b', {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(API_LISTINGS_TIMEOUT),
    });
  return response;
}

async function getModelByMakeWithCounts({
  section,
  make,
  payload,
  headers = { accept: 'application/json' },
  timeout,
}: GetModelsWithCountsRequest): Promise<
  AxiosResponse<IGetSearchFilterMakeModel>
> {
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.post(
      `/ddapi/v4/search/filters/${section}/${make}`,
      payload,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );
  return response;
}

async function getModelByMake(
  section: string,
  make: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  dealerId: string | null = null,
  timeout?: string,
): Promise<AxiosResponse<IGetSearchFilterMakeModel>> {
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.get(
      dealerId
        ? `/ddapi/v1/search/showroom/filters/${section}/${make}?dealerId=${dealerId}`
        : `/ddapi/v1/search/filters/${section}/${make}`,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );

  return response;
}

async function getTrimByMakeModel(
  section: string,
  make: string,
  model: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: string,
): Promise<AxiosResponse<IGetSearchFilterMakeModel>> {
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.get(
      `/ddapi/v4/search/filters/${section}/${make}/${model}`,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );
  return response;
}

async function getTrimByMakeModelWithCount({
  section,
  make,
  model,
  payload,
  headers = { accept: 'application/json' },
  timeout,
}: GetTrimsWithCountsRequest): Promise<
  AxiosResponse<IGetSearchFilterMakeModel>
> {
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.post(
      `/ddapi/v4/search/filters/${section}/${make}/${model}`,
      payload,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );
  return response;
}

async function getSections(
  section = 'all',
  payload: IGetAdsRequest,
  timeout?: string,
): Promise<AxiosResponse<IGetSection>> {
  const response: AxiosResponse<IGetSection> =
    await donedealServicesGateway.post(
      `/ddapi/v1/search/sections/${section}`,
      payload,
      {
        headers: {
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );

  return response;
}

export const searchPageApi = {
  getAds,
  getAdsV2,
  getFilters,
  getFiltersWithCount,
  getFiltersC2B,
  getFiltersDealerShowroom,
  getModelByMake,
  getModelByMakeWithCounts,
  getTrimByMakeModel,
  getTrimByMakeModelWithCount,
  getSections,
  getCarsFiltersWithCount,
};
